import type { ExprNode } from '@revelio/data-access';
import { PositionCategoryKind, UserCategoryKind } from '@revelio/data-access';
import type { SelectionList, ValidValueTypes } from '@revelio/iso-utility';

import type { FilterDataResult } from '../../contexts/FilterProvider';
import { getFilterDefinition } from '../../engine/filterRegistry';
import type {
  ListFilter,
  SchoolFilter,
  StaticListFilter,
  TreeFilter,
} from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';
import { collectValLeaves } from './expr';
import {
  dedupeListSelections,
  dedupeTreeSelections,
  upsertInstance,
} from './instances';
import {
  buildCompanyTreePathId,
  type CompanyRics,
  getCompanySelectionListId,
  getPositionGeoSelectionListId,
  getRoleSelectionListId,
  getSkillSelectionListId,
  getUserGeoSelectionListId,
  resolveSelectionListLabelById,
  resolveStaticListLabelById,
  resolveTreeSelectionFromFilterData,
  rollUpRicsK50ToK10,
} from './selection';
import type { AiTransformUnknownFilter } from './types';

/**
 * Converts value leaves (`ExprNode.val`) into CustomDash filter instances.
 *
 * The AI expression contains two "value" shapes:
 * - `val.pos_attribute`  → company/role/geo/school/seniority/static lists
 * - `val.user_attribute` → user geo / skills / list filters (gender, ethnicity, education)
 *
 * For tree filters, we try to resolve the correct nested tree id using `getFilterData`
 * (e.g. "14_60_233459") and fall back to selection list labels or raw ids when needed.
 */
export const transformValueLeavesInConjunct = ({
  conjunct,
  isNegated,
  selectionLists,
  getFilterData,
  unknownFilters,
  groupTreeInstances,
  groupListInstances,
  groupStaticInstances,
  groupSchoolInstances,
  companyNameByRcid,
  companyRicsByRcid,
}: {
  conjunct: ExprNode;
  isNegated: boolean;
  selectionLists: SelectionList<ValidValueTypes>[];
  getFilterData: (filterId: FilterId) => FilterDataResult;
  unknownFilters: AiTransformUnknownFilter[];
  groupTreeInstances: Map<FilterId, TreeFilter[]>;
  groupListInstances: Map<FilterId, ListFilter[]>;
  groupStaticInstances: Map<FilterId, StaticListFilter[]>;
  groupSchoolInstances: Map<FilterId, SchoolFilter[]>;
  companyNameByRcid: Map<number, string>;
  companyRicsByRcid: Map<number, CompanyRics>;
}): void => {
  const valLeaves = collectValLeaves(conjunct, isNegated);
  for (const leaf of valLeaves) {
    const pos = leaf.val.pos_attribute;
    if (pos) {
      // The COMPANY tree only has rics_k10 / rics_k400 / company levels, so roll
      // K50 industries returned by AI search up to their K10 parent. Falls back
      // to the original K50 if the lookup fails (chip will render invalid).
      let effectiveKind = pos.kind;
      let effectiveId = pos.id;
      if (pos.kind === PositionCategoryKind.RicsK50) {
        const k10 = rollUpRicsK50ToK10({
          k50NumericId: pos.id,
          selectionLists,
        });
        if (k10) {
          effectiveKind = PositionCategoryKind.RicsK10;
          effectiveId = k10.numericId;
        }
      }

      // Company/Industry
      const companyLevelId = getCompanySelectionListId(effectiveKind);
      if (companyLevelId) {
        const treeDefinition = getFilterDefinition(FilterId.COMPANY);
        if (treeDefinition && treeDefinition.type === FilterType.TREE) {
          const treeResult = resolveTreeSelectionFromFilterData({
            filterId: FilterId.COMPANY,
            selectionListId: companyLevelId,
            numericId: effectiveId,
            getFilterData,
          });
          const selListLabel = resolveSelectionListLabelById({
            selectionListId: companyLevelId,
            numericId: effectiveId,
            selectionLists,
          });
          // companyNameByRcid is keyed by RCID; only consult for actual company
          // selections. A small industry numeric id can collide with a real RCID
          // in the response and mislabel the industry chip with a company name.
          const companyNameFromResponse =
            effectiveKind === PositionCategoryKind.CompanyId
              ? companyNameByRcid.get(effectiveId)
              : undefined;

          const treeResolved =
            treeResult ??
            (() => {
              const label =
                companyNameFromResponse ?? selListLabel ?? String(effectiveId);
              // The default tree usually lacks an AI-resolved company's rcid, so
              // build the path-encoded id (`${k10}_${k400}_${rcid}`) from the
              // response's RICS ancestry. That lets the adaptive role/skill
              // taxonomy resolve from the company's k400 → k50 parent natively,
              // exactly like a company-search pick. Falls back to the bare rcid
              // when RICS is absent (taxonomy then stays default).
              const pathId =
                effectiveKind === PositionCategoryKind.CompanyId
                  ? buildCompanyTreePathId(
                      effectiveId,
                      companyRicsByRcid.get(effectiveId)
                    )
                  : null;
              return { id: pathId ?? String(effectiveId), name: label };
            })();

          upsertInstance(
            groupTreeInstances,
            FilterId.COMPANY,
            (f) =>
              (f.isCurrent ?? true) === (pos.curr ?? true) &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(treeDefinition as TreeFilter),
              selections: [
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: companyLevelId,
                },
              ],
              isCurrent: pos.curr ?? true,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeTreeSelections([
                ...existing.selections,
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: companyLevelId,
                },
              ]),
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Role
      const roleLevelId = getRoleSelectionListId(pos.kind);
      if (roleLevelId) {
        const treeDefinition = getFilterDefinition(FilterId.ROLE);
        if (treeDefinition && treeDefinition.type === FilterType.TREE) {
          // Intentionally DO NOT resolve the role name here. Role labels
          // are taxonomy-dependent — the same numeric id means different
          // roles under different `ROLE_TAXONOMY` values (e.g. id `8524`
          // at k15000 is "Software Developer" under `{default: true}` but
          // "Steel Fixer" under `{ricsK50Id: '31'}`). Both `getFilterData`
          // and `selectionLists` reflect the CURRENT (pre-navigation)
          // filter-state / UserPreferencesContext scope — which during
          // a cross-scope AI apply (e.g. /company/pay → /role/transitions)
          // is the WRONG scope. Resolving here produces a label that looks
          // valid but is wrong, and because it looks valid,
          // `applyResolvedSelectionLabelsToFilterMap`'s
          // `selectionNameLooksUnresolved` check skips it post-REPLACE_FILTERS
          // and the stale name is never corrected.
          //
          // Leaving `name` as the raw numeric id makes the selection
          // "unresolved-looking" so the label patcher re-resolves it via
          // the flat selection list AFTER `REPLACE_FILTERS` has settled
          // the taxonomy and FilterProvider's role-selection-list query
          // has fetched the correct-scope tree. The flat-list lookup by
          // numeric id at a specific level is unambiguous within a given
          // taxonomy (each k15000/k150/etc. item has a unique id at that
          // level), so the patcher gets the correct label. `id` also
          // stays raw numeric here — tree-encoded paths break the flat
          // lookup (`filterId: ROLE, split('_').pop()` would pick the
          // wrong leaf across path-encoded vs flat selection lists).
          const treeResolved = {
            id: String(pos.id),
            name: String(pos.id),
          };

          upsertInstance(
            groupTreeInstances,
            FilterId.ROLE,
            (f) =>
              (f.isCurrent ?? true) === (pos.curr ?? true) &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(treeDefinition as TreeFilter),
              selections: [
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: roleLevelId,
                },
              ],
              isCurrent: pos.curr ?? true,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeTreeSelections([
                ...existing.selections,
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: roleLevelId,
                },
              ]),
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Geography (position/work location)
      const geoPosLevelId = getPositionGeoSelectionListId(pos.kind);
      if (geoPosLevelId) {
        const treeDefinition = getFilterDefinition(FilterId.GEOGRAPHY);
        if (treeDefinition && treeDefinition.type === FilterType.TREE) {
          // For position geography we mark `isCurrent=false` (i.e. "work/position location").
          const treeResolved =
            resolveTreeSelectionFromFilterData({
              filterId: FilterId.GEOGRAPHY,
              selectionListId: geoPosLevelId,
              numericId: pos.id,
              getFilterData,
            }) ??
            (() => {
              const label =
                resolveSelectionListLabelById({
                  selectionListId: geoPosLevelId,
                  numericId: pos.id,
                  selectionLists,
                }) ?? String(pos.id);
              return { id: String(pos.id), name: label };
            })();

          upsertInstance(
            groupTreeInstances,
            FilterId.GEOGRAPHY,
            (f) =>
              (f.isCurrent ?? true) === false &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(treeDefinition as TreeFilter),
              selections: [
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: geoPosLevelId,
                },
              ],
              isCurrent: false,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeTreeSelections([
                ...existing.selections,
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: geoPosLevelId,
                },
              ]),
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // School
      if (pos.kind === PositionCategoryKind.Rsid) {
        const definition = getFilterDefinition(FilterId.SCHOOL);
        if (definition && definition.type === FilterType.SCHOOL) {
          // School filters are keyed by RSID. We initially store RSID as the label/name,
          // then `resolveSchoolNamesInPlace` replaces those labels using `response.filters.schoolNames`.
          const rsid = String(pos.id);
          const key = rsid;

          upsertInstance(
            groupSchoolInstances,
            FilterId.SCHOOL,
            (f) => (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(definition as SchoolFilter),
              schoolResultItems: {
                [key]: {
                  label: rsid,
                  name: rsid,
                  primary_name: rsid,
                  rsid,
                },
              },
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              schoolResultItems: {
                ...existing.schoolResultItems,
                [key]: {
                  label: rsid,
                  name: rsid,
                  primary_name: rsid,
                  rsid,
                },
              },
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Seniority (list)
      if (pos.kind === PositionCategoryKind.Seniority) {
        const definition = getFilterDefinition(FilterId.SENIORITY);
        if (definition && definition.type === FilterType.LIST) {
          const isCurrent = pos.curr ?? true;
          const selectionName =
            resolveSelectionListLabelById({
              selectionListId: 'seniority',
              numericId: pos.id,
              selectionLists,
            }) ?? String(pos.id);

          upsertInstance(
            groupListInstances,
            FilterId.SENIORITY,
            (f) =>
              (f.isCurrent ?? true) === isCurrent &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(definition as ListFilter),
              selections: [{ id: String(pos.id), name: selectionName }],
              isCurrent,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeListSelections([
                ...existing.selections,
                { id: String(pos.id), name: selectionName },
              ]),
              isCurrent,
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Employment relationship / work schedule (list filters backed by a
      // fixed selection list, like seniority).
      const fixedListConfig = (() => {
        switch (pos.kind) {
          case PositionCategoryKind.EmploymentRelationship:
            return {
              filterId: FilterId.EMPLOYMENT_RELATIONSHIP,
              selectionListId: 'employment_relationship',
            };
          case PositionCategoryKind.WorkSchedule:
            return {
              filterId: FilterId.WORK_SCHEDULE,
              selectionListId: 'work_schedule',
            };
          default:
            return null;
        }
      })();

      if (fixedListConfig) {
        const definition = getFilterDefinition(fixedListConfig.filterId);
        if (definition && definition.type === FilterType.LIST) {
          const isCurrent = pos.curr ?? true;
          const selectionName =
            resolveSelectionListLabelById({
              selectionListId: fixedListConfig.selectionListId,
              numericId: pos.id,
              selectionLists,
            }) ?? String(pos.id);

          upsertInstance(
            groupListInstances,
            fixedListConfig.filterId,
            (f) =>
              (f.isCurrent ?? true) === isCurrent &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(definition as ListFilter),
              selections: [{ id: String(pos.id), name: selectionName }],
              isCurrent,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeListSelections([
                ...existing.selections,
                { id: String(pos.id), name: selectionName },
              ]),
              isCurrent,
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Static lists
      const staticFilterId = (() => {
        switch (pos.kind) {
          case PositionCategoryKind.FlightRisk:
            return FilterId.FLIGHT_RISK;
          case PositionCategoryKind.Prestige:
            return FilterId.PRESTIGE;
          case PositionCategoryKind.RemoteSuitability:
            return FilterId.REMOTE_SUITABILITY;
          default:
            return null;
        }
      })();

      if (staticFilterId) {
        const definition = getFilterDefinition(staticFilterId);
        if (definition && definition.type === FilterType.STATIC_LIST) {
          const isCurrent = pos.curr ?? true;
          const selectionName = resolveStaticListLabelById({
            filterId: staticFilterId,
            numericId: pos.id,
          });

          upsertInstance(
            groupStaticInstances,
            staticFilterId,
            (f) =>
              (f.isCurrent ?? true) === isCurrent &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(definition as StaticListFilter),
              selections: [{ id: String(pos.id), name: selectionName }],
              isCurrent,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeListSelections([
                ...existing.selections,
                { id: String(pos.id), name: selectionName },
              ]),
              isCurrent,
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }
    }

    const user = leaf.val.user_attribute;
    if (user) {
      // Geography (user)
      const geoUserLevelId = getUserGeoSelectionListId(user.kind);
      if (geoUserLevelId) {
        const treeDefinition = getFilterDefinition(FilterId.GEOGRAPHY);
        if (treeDefinition && treeDefinition.type === FilterType.TREE) {
          // For user geography we mark `isCurrent=true` (i.e. "current/home location").
          const treeResolved =
            resolveTreeSelectionFromFilterData({
              filterId: FilterId.GEOGRAPHY,
              selectionListId: geoUserLevelId,
              numericId: user.id,
              getFilterData,
            }) ??
            (() => {
              const label =
                resolveSelectionListLabelById({
                  selectionListId: geoUserLevelId,
                  numericId: user.id,
                  selectionLists,
                }) ?? String(user.id);
              return { id: String(user.id), name: label };
            })();

          upsertInstance(
            groupTreeInstances,
            FilterId.GEOGRAPHY,
            (f) =>
              (f.isCurrent ?? true) === true &&
              (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(treeDefinition as TreeFilter),
              selections: [
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: geoUserLevelId,
                },
              ],
              isCurrent: true,
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeTreeSelections([
                ...existing.selections,
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: geoUserLevelId,
                },
              ]),
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Skills (tree)
      const skillLevelId = getSkillSelectionListId(user.kind);
      if (skillLevelId) {
        const definition = getFilterDefinition(FilterId.CUSTOM_SKILL);
        if (definition && definition.type === FilterType.TREE) {
          // Skills are also a tree filter; the kind tells us which taxonomy level list to use.
          const treeResolved =
            resolveTreeSelectionFromFilterData({
              filterId: FilterId.CUSTOM_SKILL,
              selectionListId: skillLevelId,
              numericId: user.id,
              getFilterData,
            }) ??
            (() => {
              const label =
                resolveSelectionListLabelById({
                  selectionListId: skillLevelId,
                  numericId: user.id,
                  selectionLists,
                }) ?? String(user.id);
              return { id: String(user.id), name: label };
            })();

          upsertInstance(
            groupTreeInstances,
            FilterId.CUSTOM_SKILL,
            (f) => (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(definition as TreeFilter),
              selections: [
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: skillLevelId,
                },
              ],
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeTreeSelections([
                ...existing.selections,
                {
                  id: treeResolved.id,
                  name: treeResolved.name,
                  levelId: skillLevelId,
                },
              ]),
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }

      // Gender / Ethnicity / Highest degree
      const listFilterId = (() => {
        switch (user.kind) {
          case UserCategoryKind.Gender:
            return FilterId.GENDER;
          case UserCategoryKind.Ethnicity:
            return FilterId.ETHNICITY;
          case UserCategoryKind.HighestDegree:
            return FilterId.EDUCATION;
          default:
            return null;
        }
      })();

      if (listFilterId) {
        const definition = getFilterDefinition(listFilterId);
        if (definition && definition.type === FilterType.LIST) {
          const selectionListId = (definition as ListFilter).selectionListId;
          const selectionName =
            resolveSelectionListLabelById({
              selectionListId,
              numericId: user.id,
              selectionLists,
            }) ?? String(user.id);

          upsertInstance(
            groupListInstances,
            listFilterId,
            (f) => (f.isNot ?? false) === leaf.isNegated,
            () => ({
              ...(definition as ListFilter),
              selections: [{ id: String(user.id), name: selectionName }],
              isNot: leaf.isNegated,
              isVisible: true,
              isUserSubmitted: true,
            }),
            (existing) => ({
              ...existing,
              selections: dedupeListSelections([
                ...existing.selections,
                { id: String(user.id), name: selectionName },
              ]),
              isUserSubmitted: true,
            })
          );
        }
        continue;
      }
    }

    // If we got here, we couldn't map this leaf into any known filter.
    // We return it as "unknown" so the UI can still show feedback to the user.
    unknownFilters.push({
      name: 'unknown',
      relevantPromptText: '',
      explanation: 'Unsupported filter in AI response',
    });
  }
};
